import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useState, useEffect } from "react";
import { IoPersonCircle } from "react-icons/io5";
import { RiLogoutBoxRLine } from "react-icons/ri";
import { IoMdArrowDropdown } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { clearToken } from "@/store/token.slice";
import { clearUser } from "@/store/user.slice";
import { clearResturant } from "@/store/resturant.slice";
import { clearApps } from "@/store/apps.slice";
import { clearServiceWorkers } from "@/utils/helpers";
import { RootState } from "@/store/store";
import { useUserRole } from "@/hooks/useUserRole";
import ProfileImage from "../ProfileImage";
import { Capacitor } from "@capacitor/core";
import { clearWarning } from "@/store/warningSlice";
import { clearStorageAllPlatforms } from "@/utils/LocalStorageForAllPlatforms";


const UserDropdown = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const userData = useSelector((state: RootState) => state.user.userData);
  const userAvatar = userData?.avatar || null
  const userName = userData?.username || "User";
  const { RoleNamesEnAr } = useUserRole();
  const [isOpen, setIsOpen] = useState(false);
  const token = useSelector((state: { auth: { token: string } }) => state.auth.token);


  const toggleDropdown = () => setIsOpen((prev) => !prev);

  const closeDropdown = (e: Event) => {
    const target = e.target as HTMLElement;
    if (!target.closest(".dropdown")) {
      setIsOpen(false);
    }
  };

  const handleProfilePageNavigate = () => {
    navigate("/settings/user-settings");
    setIsOpen(false);
  };

  const logout = async () => {
    const toastId = toast.loading(t("userDropdown.loggingOut"));
    await clearServiceWorkers(token);

    setTimeout(() => {
      dispatch(clearToken());
      dispatch(clearUser());
      dispatch(clearResturant());
      dispatch(clearApps());
      dispatch(clearWarning());
      // navigate("/login");
      toast.dismiss(toastId);
    }, 1000);
    // localStorage.clear();
    await clearStorageAllPlatforms()

    if (Capacitor.getPlatform() === "web") {
      window.location.href = '/login'
    } else {
      navigate('/login')
    }
  };

  useEffect(() => {
    window.addEventListener("click", closeDropdown);
    return () => window.removeEventListener("click", closeDropdown);
  }, []);

  return (
    <div className="dropdown relative md:ms-[1px] lg:inline-block">
      <button
        id="menu-button"
        className="dropdown-button group z-10 flex items-center gap-2 rounded-md p-2 text-gray-600 duration-300 hover:bg-black/10"
        onClick={(e) => {
          e.stopPropagation();
          toggleDropdown();
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <ProfileImage
          size={30}
          imageUrl={userAvatar}
          iconClassName="text-white md:text-gray-500 max-h-[25px] max-w-[25px] md:max-h-[30px] md:max-w-[30px] md:text-gray-500 md:group-hover:text-primary "
        />

        <div className="flex items-center gap-2">
          {/* Name & Role */}
          <div className="mt-1 hidden flex-col items-center justify-center text-nowrap text-start text-xs font-semibold md:flex md:group-hover:text-primary">
            <p className="w-full">{userName}</p>
            <p className="w-full">
              {locale === "en" ? RoleNamesEnAr.en : RoleNamesEnAr.ar}
            </p>
          </div>
          <IoMdArrowDropdown
            className={`transform text-white md:text-gray-500 md:group-hover:text-primary ${isOpen ? "rotate-180" : "rotate-0"}`}
          />
        </div>
      </button>

      {isOpen && (
        <div
          id="dropdown-menu"
          className="absolute start-[-110px] z-[9999] mt-2 w-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 md:start-[5px]"
          role="menu"
        >
          <button
            type="button"
            className="flex w-full items-center px-1 py-2 text-gray-500 hover:bg-gray-100"
            onClick={handleProfilePageNavigate}
          >
            <IoPersonCircle className="mx-1 mr-2 text-xl text-gray-500" />
            {t("userDropdown.profile")}
          </button>
          <button
            type="button"
            className="flex w-full items-center px-1 py-2 text-gray-500 hover:bg-gray-100"
            onClick={logout}
          >
            <RiLogoutBoxRLine className="mx-1 mr-2 text-lg text-red-600" />
            {t("userDropdown.logout")}
          </button>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
